import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PaletteIcon from '@mui/icons-material/Palette';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions'; //Import your actions
import { getAvailableThemes, readThemeColorsForScheme } from '../utils/themeUtils';

const THEME_GROUPS = [
  { id: 'cool', label: 'Cool', themes: ['midnight', 'navy', 'ocean', 'slate'] },
  { id: 'violet', label: 'Violet', themes: ['lavender', 'purple', 'beatsaber'] },
  { id: 'green', label: 'Green', themes: ['green', 'moss'] },
  { id: 'warm', label: 'Warm', themes: ['copper', 'coral', 'rose', 'burgundy', 'pink'] },
];

const FALLBACK_SWATCHES = {
  purple: { dark: '#4a3b6b', accent: '#6b4b8a' },
  ocean: { dark: '#1a5f7a', accent: '#2b7a94' },
  green: { dark: '#2d5a27', accent: '#3d6a37' },
  navy: { dark: '#1B3B6F', accent: '#2b4b8f' },
  burgundy: { dark: '#6B2E3B', accent: '#8b3e4b' },
  slate: { dark: '#2C3E50', accent: '#3c4e60' },
  pink: { dark: '#aa2482', accent: '#c73d9a' },
  lavender: { dark: '#483D8B', accent: '#6A5ACD' },
  coral: { dark: '#CD5C5C', accent: '#DC143C' },
  copper: { dark: '#B87333', accent: '#CD853F' },
  rose: { dark: '#8B0000', accent: '#B22222' },
  midnight: { dark: '#191970', accent: '#483D8B' },
  moss: { dark: '#2d5a27', accent: '#c45c26' },
  beatsaber: { dark: '#430259', accent: '#c45c26' },
};

const themeLabel = (name) => name.charAt(0).toUpperCase() + name.slice(1);

const organizeThemes = (available) => {
  const availableSet = new Set(available);
  const used = new Set();

  const groups = THEME_GROUPS
    .map((group) => ({
      ...group,
      themes: group.themes.filter((name) => availableSet.has(name)),
    }))
    .filter((group) => group.themes.length > 0);

  groups.forEach((group) => {
    group.themes.forEach((name) => used.add(name));
  });

  const rest = available.filter((name) => !used.has(name));
  if (rest.length) {
    groups.push({ id: 'more', label: 'More', themes: rest });
  }

  return groups;
};

const swatchForTheme = (themeName) => {
  const live = readThemeColorsForScheme(themeName);
  const fallback = FALLBACK_SWATCHES[themeName] || {};

  return {
    dark: live?.dark || fallback.dark || '#6b7686',
    accent: live?.accent1 || fallback.accent || live?.dark || '#6b7686',
  };
};

const GRADIENT_CONFIG = { // for sheen anim
  colors: [
    { offset: '0%', color: 'rgb(34, 139, 34)' },    // Forest Green
    { offset: '25%', color: 'rgb(0, 128, 128)' },   // Teal
    { offset: '50%', color: 'rgb(0, 255, 127)' },   // Spring Green
    { offset: '75%', color: 'rgb(0, 128, 128)' },   // Teal
    { offset: '100%', color: 'rgb(34, 139, 34)' }   // Forest Green
  ],
  angle: {
    y1: '20%',
    y2: '80%'
  },
  animation: {
    duration: '1s'
  }
};

const PICKER_EDGE_PAD = 8;

const clampPickerToViewport = (element) => {
  if (!element) return;

  element.style.setProperty('--picker-dx', '0px');
  element.style.setProperty('--picker-dy', '0px');

  const rect = element.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let dx = 0;
  let dy = 0;

  if (rect.right > vw - PICKER_EDGE_PAD) {
    dx -= rect.right - (vw - PICKER_EDGE_PAD);
  }
  if (rect.left + dx < PICKER_EDGE_PAD) {
    dx += PICKER_EDGE_PAD - (rect.left + dx);
  }
  if (rect.bottom > vh - PICKER_EDGE_PAD) {
    dy -= rect.bottom - (vh - PICKER_EDGE_PAD);
  }
  if (rect.top + dy < PICKER_EDGE_PAD) {
    dy += PICKER_EDGE_PAD - (rect.top + dy);
  }

  element.style.setProperty('--picker-dx', `${dx}px`);
  element.style.setProperty('--picker-dy', `${dy}px`);
};

const ColorSchemePicker = (props) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const containerRef = useRef(null);
    
  // Allow external control of picker state via isOpen prop
  const isPickerOpen = props.isOpen !== undefined ? props.isOpen : pickerOpen;

  const activeColor = () => {
    return props.user[`${props.page}_color_scheme`];
  }

  const setColorScheme = (e, color) => {
    e.preventDefault();
    e.stopPropagation();
    
    props.updateColorScheme({
      [`${props.page}_color_scheme`]: color
    }, props.authToken);
  }

  const handleClick = () => {
    if (props.isOpen !== undefined) {
      // External control - call the callback if provided
      if (props.onToggle) {
        props.onToggle(!isPickerOpen);
      }
    } else {
      // Internal control
      setPickerOpen(!pickerOpen);
    }
  };

  const themeGroups = useMemo(
    () => (isPickerOpen ? organizeThemes(getAvailableThemes()) : []),
    [isPickerOpen]
  );

  useLayoutEffect(() => {
    if (!isPickerOpen) return undefined;

    const clamp = () => clampPickerToViewport(containerRef.current);
    clamp();
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, [isPickerOpen, themeGroups]);

  return (
    <div className={`theme-toggle ${props.className}`} onClick={handleClick}>
      <svg width="0" height="0" aria-hidden="true" className="palette-gradient-defs">
        <defs>
          <linearGradient id="palette-gradient" x1="-150%" y1={GRADIENT_CONFIG.angle.y1} x2="150%" y2={GRADIENT_CONFIG.angle.y2}>
            {GRADIENT_CONFIG.colors.map((stop, index) => (
              <stop key={index} offset={stop.offset} stopColor={stop.color} />
            ))}
            <animate
              attributeName="x1"
              values="-150%;150%"
              dur={GRADIENT_CONFIG.animation.duration}
              repeatCount="indefinite"
            />
            <animate
              attributeName="x2"
              values="-50%;250%"
              dur={GRADIENT_CONFIG.animation.duration}
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
      </svg>

      <PaletteIcon 
        className={`nav-icon${isPickerOpen ? ' picker-open' : ''}`}
        sx={{ 
          fill: isPickerOpen ? 'url(#palette-gradient)' : 'currentColor',
          '&:hover': {
            fill: 'url(#palette-gradient)'
          },
          '&:active': {
            fill: 'url(#palette-gradient)'
          }
        }}
      />

      {isPickerOpen && (
        <div
          ref={containerRef}
          className={`color-options-container ${props.placement || 'below'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {themeGroups.map((group) => (
            <div key={group.id} className="color-option-group">
              <span className="color-option-group-label">{group.label}</span>
              <div className="color-option-row">
                {group.themes.map((themeName) => {
                  const swatch = swatchForTheme(themeName);
                  return (
                    <button
                      key={themeName}
                      type="button"
                      className={`color-option option-${themeName} ${activeColor() === themeName ? 'selected' : ''}`}
                      style={{
                        '--swatch-dark': swatch.dark,
                        '--swatch-accent': swatch.accent,
                      }}
                      title={themeLabel(themeName)}
                      aria-label={themeLabel(themeName)}
                      aria-pressed={activeColor() === themeName}
                      data-color={themeName}
                      onClick={(e) => setColorScheme(e, themeName)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {props.children}
    </div>
  );
};

function mapStateToProps(state, props) {
  return {
    authToken: state.authToken,
    user: state.user
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ColorSchemePicker);
